import { supabase } from 'lib/supabase';
import { worstEstado } from 'lib/estado';
import { INSTALACION_DEFAULT } from 'data/checklist';
import { enqueue, listPending, removeJob, registrarIntento } from 'lib/offlineQueue';
import { modoPin, leerMeta } from 'lib/pinStorage';
import { anularInspeccion } from 'services/adminService';
import { idsArboles, normalizarArboles } from 'lib/arbolesCache';
import { idsLineas, normalizarLineas } from 'lib/lineasCache';

const TABLE = 'cables_inspecciones';
const BUCKET = 'cables-evidencia';

export const fetchGuias = async () => {
  const { data, error } = await supabase?.from('cables_guias')?.select('nombre')?.order('nombre');
  if (error) throw error;
  return (data || []).map((g) => g.nombre);
};
export const addGuia = async (nombre) => {
  const n = (nombre || '').trim();
  if (!n) return;
  await supabase?.from('cables_guias')?.upsert({ nombre: n }, { onConflict: 'nombre' });
};

// Árboles activos de una instalación para elegir en el ítem fitosanitario. La
// rpc es SECURITY DEFINER (la cuenta PIN no lee la tabla) y devuelve solo id,
// codigo, especie, sostiene e instalacion. Lanza si falla: quien llama sigue
// con el caché o sin pedir árbol.
export const fetchArbolesInspeccion = async (instalacion) => {
  const { data, error } = await supabase.rpc('cables_arboles_para_inspeccion', {
    p_instalacion: instalacion || INSTALACION_DEFAULT,
  });
  if (error) throw error;
  return normalizarArboles(data);
};

// Cables activos de una instalación para "Estado del cable" y "Punto de
// anclaje". Igual que los árboles: rpc SECURITY DEFINER (también para la
// cuenta PIN) con id, codigo, nombre, plataformas e instalacion. Lanza si falla.
export const fetchLineasInspeccion = async (instalacion) => {
  const { data, error } = await supabase.rpc('cables_lineas_para_inspeccion', {
    p_instalacion: instalacion || INSTALACION_DEFAULT,
  });
  if (error) throw error;
  return normalizarLineas(data);
};

const textoDe = (e) => `${e?.message || ''} ${e?.error || ''} ${e?.details || ''}`.trim();

const isNetworkError = (e) => {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  const t = `${textoDe(e)} ${e?.originalError?.message || ''}`;
  return /failed to fetch|networkerror|network request failed|load failed|fetch failed/i.test(t)
    || e?.name === 'TypeError' || e?.originalError?.name === 'TypeError';
};

// Separa lo que se arregla solo reintentando (red, servidor caído, token
// vencido que el cliente refresca) de lo que la base rechaza siempre igual
// (permisos, CHECK, trigger). Lo permanente queda en la cola con su motivo a la
// vista: nunca desaparece en silencio ni se reintenta en bucle.
export const clasificarError = (e) => {
  const codigo = String(e?.code || '');
  const estado = Number(e?.statusCode ?? e?.status);
  const mensaje = textoDe(e) || String(e || '');
  const detalle = [codigo, Number.isFinite(estado) && estado ? `HTTP ${estado}` : '', mensaje].filter(Boolean).join(' · ');
  if (isNetworkError(e)) return { tipo: 'red', motivo: 'Sin conexión con el servidor.', detalle };
  if (estado >= 500 || estado === 408 || estado === 429) {
    return { tipo: 'red', motivo: 'El servidor no respondió. Se reintentará.', detalle };
  }
  if (codigo === 'PGRST301' || /jwt expired/i.test(mensaje)) {
    return { tipo: 'red', motivo: 'La sesión se estaba renovando. Se reintentará.', detalle };
  }
  let motivo;
  if (codigo === '42501' || /row-level security|permission denied/i.test(mensaje)) {
    motivo = 'La base no aceptó la inspección por permisos (fecha fuera del rango permitido o sesión de PIN vencida).';
  } else if (codigo === '23514' || /check constraint/i.test(mensaje)) {
    motivo = 'La base no aceptó la inspección: está incompleta o tiene valores inválidos.';
  } else if (codigo === 'P0001' && mensaje) {
    motivo = `La base no aceptó la inspección: ${mensaje}`;
  } else {
    motivo = `La base no aceptó la inspección${mensaje ? `: ${mensaje}` : '.'}`;
  }
  return { tipo: 'permanente', motivo, detalle };
};

const errorLegible = (e) => {
  const c = clasificarError(e);
  return Object.assign(new Error(c.motivo), { motivo: c.motivo, detalle: c.detalle, tipo: c.tipo, original: e });
};

const withTz = (naive) => {
  if (!naive) return new Date().toISOString();
  if (naive.includes('+') || naive.includes('Z') || /[+-]\d{2}:\d{2}$/.test(naive)) return naive;
  return naive + '-06:00';
};

// Reenvío idempotente para TODOS los roles: nadie tiene UPDATE en la tabla
// (el historial es inmutable) ni en Storage. Se sube sin upsert y se guarda con
// insert ... on conflict do nothing: un reenvío de la cola encuentra la fila u
// objeto ya creados y lo da por hecho.
const yaExiste = (error) => {
  const estado = String(error?.statusCode ?? error?.status ?? '');
  const texto = `${error?.message || ''} ${error?.error || ''} ${error?.code || ''}`.toLowerCase();
  return estado === '409' || String(error?.code ?? '') === '23505' || texto.includes('already exists') || texto.includes('duplicate')
    || texto.includes('resourcealreadyexists');
};

const uploadBlob = async (path, blob, contentType) => {
  const { error } = await supabase.storage.from(BUCKET).upload(path, blob, { contentType, upsert: false });
  if (error && !yaExiste(error)) throw error;
  return path;
};

const guardarFila = (fila) => supabase.from(TABLE).upsert(fila, { onConflict: 'id', ignoreDuplicates: true });

// detalles {key:{nota, fotoBlobs:[Blob]}} -> DB {key:{nota, fotos:[paths]}}
const uploadDetalles = async (id, detalles) => {
  const out = {};
  for (const key of Object.keys(detalles || {})) {
    const d = detalles[key] || {};
    const fotos = [];
    const blobs = d.fotoBlobs || [];
    for (let i = 0; i < blobs.length; i++) {
      fotos.push(await uploadBlob(`${id}/item-${key}-${i + 1}.jpg`, blobs[i], 'image/jpeg'));
    }
    out[key] = { nota: (d.nota || '').trim(), fotos };
    // Ítem fitosanitario: el o los árboles elegidos (ids), solo si hay alguno.
    const arboles = idsArboles(d.arboles);
    if (arboles.length) out[key].arboles = arboles;
    // Estado del cable y punto de anclaje: el o los cables elegidos (ids).
    const lineas = idsLineas(d.lineas);
    if (lineas.length) out[key].lineas = lineas;
  }
  return out;
};

const uploadEvidencia = async (id, src) => {
  let firma_url = null;
  if (src.firmaBlob) firma_url = await uploadBlob(`${id}/firma.png`, src.firmaBlob, 'image/png');
  const fotos = [];
  for (let i = 0; i < (src.fotoBlobs || []).length; i++) {
    fotos.push(await uploadBlob(`${id}/foto-${i + 1}.jpg`, src.fotoBlobs[i], 'image/jpeg'));
  }
  const detalles = await uploadDetalles(id, src.detalles);
  return { firma_url, fotos, detalles };
};

// Hora de la primera marca (ms o ISO) -> ISO; sin dato o inválida, no va.
const aIso = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

// Con PIN, la fila lleva la sesión de PIN del momento: la base pone nombre y
// colaborador desde ahí. Un job de la cola conserva la suya aunque se envíe
// después con otra sesión (si no, se le atribuiría a otra persona). La fila
// lleva también su instalación: la cola la conserva tal cual.
const buildRecord = (id, form) => {
  const r = {
    id,
    fecha: form.fecha,
    fecha_hora: withTz(form.fechaHora),
    guia_nombre: (form.guiaNombre || '').trim(),
    resultados: form.resultados || {},
    comentarios: (form.comentarios || '').trim(),
    estado_general: worstEstado(form.resultados),
    origen: 'online',
    instalacion: form.instalacion || INSTALACION_DEFAULT,
  };
  const iniciada = aIso(form.iniciadaAt);
  if (iniciada) r.iniciada_at = iniciada;
  if (form.corrigeA) r.corrige_a = form.corrigeA;
  if (modoPin()) r.pin_sesion_id = leerMeta()?.sesionId ?? null;
  return r;
};

export const submitInspeccion = async (form) => {
  const id = crypto.randomUUID();
  const record = buildRecord(id, form);
  try {
    const { firma_url, fotos, detalles } = await uploadEvidencia(id, form);
    const { error } = await guardarFila({ ...record, firma_url, fotos, detalles });
    // 23505/409 por id duplicado: la fila ya estaba (reenvío). Es éxito.
    if (error && !yaExiste(error)) throw error;
    return { ok: true, offline: false, id };
  } catch (e) {
    if (clasificarError(e).tipo === 'red') {
      await enqueue({ id, record, firmaBlob: form.firmaBlob, fotoBlobs: form.fotoBlobs, detalles: form.detalles });
      return { ok: true, offline: true, id };
    }
    throw errorLegible(e);
  }
};

const haySesion = async () => {
  try {
    const { data } = (await supabase?.auth?.getSession()) || {};
    return !!data?.session?.user;
  } catch (e) {
    return false;
  }
};

const enviarJob = async (job) => {
  try {
    const { firma_url, fotos, detalles } = await uploadEvidencia(job.id, {
      firmaBlob: job.firmaBlob, fotoBlobs: job.fotoBlobs, detalles: job.detalles,
    });
    const { error } = await guardarFila({ ...job.record, origen: 'offline-sync', firma_url, fotos, detalles });
    if (error && !yaExiste(error)) throw error;
    await removeJob(job.id);
    return true;
  } catch (e) {
    await registrarIntento(job.id, clasificarError(e)).catch(() => {});
    return false;
  }
};

// Envía la cola. Sin sesión no se intenta nada (tras "Salir" del PIN): un
// envío anónimo lo rechazaría la base y quedaría marcado como permanente sin
// serlo. Los jobs permanentes solo se envían con reintentarTrabajo.
// Un solo envío a la vez aunque haya varios indicadores montados.
let enCurso = null;
export const flushQueue = ({ soloId } = {}) => {
  if (enCurso && !soloId) return enCurso;
  const trabajo = (async () => {
    if (!(await haySesion())) return 0;
    const jobs = await listPending();
    let synced = 0;
    for (const job of jobs) {
      if (soloId ? job.id !== soloId : job.permanente) continue;
      if (await enviarJob(job)) synced++;
    }
    return synced;
  })();
  if (soloId) return trabajo;
  enCurso = trabajo.finally(() => { enCurso = null; });
  return enCurso;
};

export const reintentarTrabajo = async (id) => {
  await registrarIntento(id, null);
  return flushQueue({ soloId: id });
};

// ── Corrección de una inspección (solo admin con SSO) ────────────────────────
// La corrección es una fila nueva con corrige_a; después se anula la original.
// Va en línea, nunca por la cola: sin conexión se dice y no se guarda nada.

// La inspección a corregir; null si no existe (o RLS no la deja ver).
export const fetchInspeccionOriginal = async (id) => {
  const { data, error } = await supabase.from(TABLE).select('*').eq('id', id).single();
  if (error) {
    if (String(error.code || '') === 'PGRST116') return null;
    throw errorLegible(error);
  }
  return data || null;
};

export const motivoAnulacionCorreccion = (nuevoId, motivo) =>
  `Corregida por ${nuevoId}: ${String(motivo || '').trim()}`;

// Anula la original con el motivo "Corregida por <id nuevo>: <motivo>".
// Nunca lanza: { ok } o { ok: false, error } para ofrecer reintentar.
export const anularOriginalCorregida = async (originalId, nuevoId, motivo) => {
  try {
    await anularInspeccion(originalId, motivoAnulacionCorreccion(nuevoId, motivo));
    return { ok: true };
  } catch (e) {
    return { ok: false, error: e?.message || 'No se pudo anular la inspección original.' };
  }
};

export const SIN_CONEXION_CORRECCION =
  'Sin conexión. La corrección necesita conexión para guardarse: sus datos siguen aquí, intente de nuevo cuando haya señal.';

// form = lo mismo que submitInspeccion + corrigeA y motivo.
// Devuelve { ok, id, anulada, errorAnulacion }. Si la fila nueva no se guarda,
// lanza (con el motivo legible) y no toca la original.
export const submitCorreccion = async (form) => {
  if (!form?.corrigeA) throw new Error('Falta la inspección a corregir.');
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    throw Object.assign(new Error(SIN_CONEXION_CORRECCION), { tipo: 'red' });
  }
  const id = crypto.randomUUID();
  const record = buildRecord(id, form);
  try {
    const { firma_url, fotos, detalles } = await uploadEvidencia(id, form);
    const { error } = await guardarFila({ ...record, firma_url, fotos, detalles });
    if (error && !yaExiste(error)) throw error;
  } catch (e) {
    if (clasificarError(e).tipo === 'red') {
      throw Object.assign(new Error(SIN_CONEXION_CORRECCION), { tipo: 'red', original: e });
    }
    throw errorLegible(e);
  }
  const an = await anularOriginalCorregida(form.corrigeA, id, form.motivo);
  return { ok: true, id, anulada: an.ok, errorAnulacion: an.ok ? null : an.error };
};
